/**
 * The consent screen. The only place a student can give another product access
 * to their study data.
 *
 * WHY THE API REDIRECTS HERE INSTEAD OF RENDERING THIS ITSELF
 * ----------------------------------------------------------
 * A consent screen served by the API would be a page that asks for trust on a
 * host the student has never seen. Here they are already signed in, on the
 * domain they use, and the browser's own address bar is doing the work that no
 * amount of reassuring copy can.
 *
 * WHAT THIS SCREEN HAS TO GET RIGHT
 * ---------------------------------
 * The client's name is chosen by whoever registered it and is rendered as TEXT,
 * never as markup: "Google" and "BreakTheTest Official" are both things a
 * stranger can call themselves. So the screen states what it can verify (where
 * the student will be sent back to) beside what it cannot (the name), rather
 * than presenting the name as though we vouched for it.
 *
 * Scopes are individually refusable. A student who wants to share what they are
 * working on without sharing their scores can do that, and the checkbox is the
 * only place that choice exists.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { loadSession } from "../lib/session.js";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";

/** Human wording for each scope. Keyed by the exact string the API sends, and a
 *  scope we do not recognize is shown verbatim rather than silently dropped:
 *  approving something unlabeled is worse than seeing an ugly string. */
const SCOPE_COPY: Record<string, { title: string; detail: string }> = {
  "insights:topics": {
    title: "What you have been practicing",
    detail:
      "The topics you have answered questions on recently, how many you got right, " +
      "and which one we would suggest working on next.",
  },
  "insights:scores": {
    title: "Your practice test scores",
    detail: "The section scores from full-length practice tests you have finished.",
  },
};

type Context = {
  client_name: string;
  destination: string;
  scopes: string[];
  verified: boolean;
  registered_days_ago: number;
};

export default function Authorize() {
  const [params] = useSearchParams();
  const tx = params.get("tx") ?? "";

  /**
   * EVERYTHING DISPLAYED COMES FROM THE SERVER, keyed by an opaque id.
   *
   * It used to come from the query string: the client's name, the scopes, the
   * destination, all handed to the browser and handed back. Anyone could open
   * this page with values of their choosing and be shown any name they liked,
   * which defeats the one thing a consent screen is for.
   */
  const [ctx, setCtx] = useState<Context | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [granted, setGranted] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tx) { setLoadError("missing"); return; }
    const sess = loadSession();
    fetch(`${API_BASE}/oauth/authorize/context?tx=${encodeURIComponent(tx)}`, {
      headers: sess?.session_id ? { Authorization: `Bearer ${sess.session_id}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("expired");
        const c = (await r.json()) as Context;
        setCtx(c);
        // Only scopes this screen can DESCRIBE start ticked. The copy for an
        // unrecognized one says "leave it off unless you know what it is", and
        // pre-ticking it made that advice contradict the checkbox beside it.
        setGranted(c.scopes.filter((s) => s in SCOPE_COPY));
      })
      .catch(() => setLoadError("expired"));
  }, [tx]);

  const requested = ctx?.scopes ?? [];
  const clientName = ctx?.client_name ?? "";
  const destination = ctx?.destination ?? null;
  const verified = ctx?.verified === true;
  const daysOld = ctx?.registered_days_ago ?? 0;

  const usable = ctx && requested.length > 0 && destination;

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    try {
      const sess = loadSession();
      const res = await fetch(`${API_BASE}/oauth/authorize/decision`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(sess?.session_id ? { Authorization: `Bearer ${sess.session_id}` } : {}),
        },
        body: JSON.stringify({ tx, approve, scopes: granted }),
      });
      const body = (await res.json()) as
        { code?: string; redirect?: string; state?: string | null; error?: string };
      if (!res.ok) {
        setError(body.error ?? "Something went wrong. Nothing was shared.");
        setBusy(false);
        return;
      }
      if (!body.redirect) {
        // Declining an app we have not reviewed ends here rather than
        // delivering the student to it.
        setDone(true);
        setBusy(false);
        return;
      }
      // The server decides where back is; the page never chooses a destination.
      const back = new URL(body.redirect);
      if (approve && body.code) back.searchParams.set("code", body.code);
      else back.searchParams.set("error", "access_denied");
      if (body.state) back.searchParams.set("state", body.state);
      window.location.replace(back.toString());
    } catch {
      setError("Could not reach the server. Nothing was shared.");
      setBusy(false);
    }
  }

  if (done) {
    return (
      <main className="mx-auto max-w-md px-5 py-16">
        <h1 className="text-xl font-semibold">Nothing was shared</h1>
        <p className="mt-3 text-sm opacity-80">
          You can close this page. If you did want to connect that app, start again from
          the app itself rather than from a link.
        </p>
      </main>
    );
  }
  if (loadError || (!ctx && !loadError)) {
    return (
      <main className="mx-auto max-w-md px-5 py-16">
        <h1 className="text-xl font-semibold">
          {loadError ? "This request has expired" : "Checking that request"}
        </h1>
        {loadError ? (
          <p className="mt-3 text-sm opacity-80">
            Approval requests are only good for a few minutes, and this one is no longer
            valid. Nothing has been shared. Go back to the app that sent you here and
            start again.
          </p>
        ) : null}
      </main>
    );
  }
  if (!usable) {
    return (
      <main className="mx-auto max-w-md px-5 py-16">
        <h1 className="text-xl font-semibold">This link is not valid</h1>
        <p className="mt-3 text-sm opacity-80">
          It is missing something it needs, so there is nothing to approve. Nothing has
          been shared.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md px-5 py-12">
      {/* THE APP NAME IS THE HEADLINE.
          An earlier version led with the same facts in the opposite order, and
          the caution about the name read louder than the name itself. A student
          scanning the screen needs to recognize who is asking before they can
          judge anything else, and "127.0.0.1:9" is not something anybody
          recognizes. The caution is still here, below, where it informs the
          decision instead of dominating it. */}
      <h1 className="text-xl font-semibold">
        {clientName || "An unnamed app"} wants to see your study data
      </h1>

      {/* AN UNREVIEWED APP SAYS SO, ABOVE EVERYTHING ELSE.
          Registration is open, which is what lets a client connect unaided and
          also means nobody vets anyone: an attacker registers, picks a name,
          and sends a student this link on our own domain. That is ordinary
          OAuth phishing and every provider lives with it, but every provider
          that lives with it also tells the user when an app is unreviewed.
          Showing a stranger the same screen as a partner we have talked to was
          the part that was ours to fix. */}
      {!verified ? (
        <div className="mt-4 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
          <strong>We have not reviewed this app.</strong> Anyone can connect an app to
          Break the Hidden Test, and nobody here has checked this one
          {daysOld <= 1 ? " — it was set up today" : null}. If you did not just ask an
          app to connect, close this page.
        </div>
      ) : null}

      {/* THE DESTINATION IS VISIBLE WITHOUT A CLICK.
          It used to sit inside a collapsed disclosure, which is precisely
          backwards: the app's name is chosen by whoever registered it, and the
          host is the one fact this page can actually verify. A student who was
          sent here by a link they did not expect has nothing else to go on, and
          hiding it behind a summary meant the only reliable signal was the one
          thing they had to go looking for. */}
      <p className="mt-3 text-sm">
        After you approve, you will be returned to <strong>{destination}</strong>.
      </p>
      <p className="mt-3 text-sm">
        It will be able to <strong>read</strong> the things you tick below, and nothing
        else. It cannot change your answers, start practice for you, or post anything.
      </p>

      <fieldset className="mt-6 space-y-3">
        <legend className="text-sm font-medium">Choose what to share</legend>
        {requested.map((s) => {
          const copy = SCOPE_COPY[s];
          const on = granted.includes(s);
          return (
            <label key={s} className="flex gap-3 rounded-lg border p-3 text-sm">
              <input
                type="checkbox"
                checked={on}
                onChange={() =>
                  setGranted((g) => (on ? g.filter((x) => x !== s) : [...g, s]))
                }
                className="mt-1 h-4 w-4"
              />
              <span>
                <span className="font-medium">{copy?.title ?? s}</span>
                <span className="mt-1 block opacity-75">
                  {copy?.detail ?? "An unrecognized permission. Leave it off unless you know what it is."}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <p className="mt-5 text-sm opacity-75">
        It will never see your name, email or password. You can disconnect at any time in
        Settings, and it stops working straight away.
      </p>

      {/* Small print, and it stays. A client picks its own name at registration,
          so "Muse" and "Google" are equally easy to claim. The destination is
          the one fact we can actually verify, and a student who was phished
          here has nothing else to go on. */}
      <p className="mt-4 text-xs opacity-70">
        Any app can choose the name shown above when it registers, so we cannot confirm
        it is who it says. The address tells you where you are sent back to, which is
        not the same as who runs the app or where your data goes afterwards. If you did
        not start this, or you do not recognize <strong>{destination}</strong>, close
        this page and nothing will be shared.
      </p>

      {error ? <p className="mt-4 text-sm text-red-500">{error}</p> : null}

      <div className="mt-7 flex gap-3">
        <button
          type="button"
          onClick={() => decide(true)}
          disabled={busy || granted.length === 0}
          className="min-h-11 flex-1 rounded-lg px-4 font-medium disabled:opacity-40"
          style={{ background: "var(--accent, #2f6fed)", color: "#fff" }}
        >
          {granted.length === 0 ? "Choose something to share" : "Approve"}
        </button>
        <button
          type="button"
          onClick={() => decide(false)}
          disabled={busy}
          className="min-h-11 rounded-lg border px-4"
        >
          Cancel
        </button>
      </div>
    </main>
  );
}
